import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { getDomainAudio, getGenerationUserId, generateAllAudioUltraFast } from '../services/domainTtsService';
import { useSession } from '../context/SessionContext';
import LocalAudioCache from '../utils/localAudioCache';
import { getFromGlobalStore } from '../utils/globalAudioStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Ordered list of domain keys for swipe navigation
const DOMAIN_ORDER = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];

// Domain configuration with icons, colors, and astro elements
const DOMAINS = {
  health: {
    key: 'health', name: 'Health & Longevity', icon: '❤️', color: '#ef4444',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Surya', color: '#FFD700' }, { symbol: '♡', label: 'Heart', color: '#FF6347' }],
      Moon: [{ symbol: '☽', label: 'Chandra', color: '#C8C8FF' }, { symbol: '◎', label: 'Mind', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Mangal', color: '#DC143C' }, { symbol: '⚠', label: 'Injury', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Budh', color: '#32CD32' }, { symbol: '◬', label: 'Nerves', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Guru', color: '#FFA500' }, { symbol: '◉', label: 'Immunity', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Shukra', color: '#FF69B4' }, { symbol: '◎', label: 'Kidney', color: '#FF1493' }],
      Saturn: [{ symbol: '♄', label: 'Shani', color: '#8B4513' }, { symbol: '◬', label: 'Bones', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Rahu', color: '#4B0082' }, { symbol: '?', label: 'Mystery', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Ketu', color: '#006400' }, { symbol: '✨', label: 'Spirit', color: '#32CD32' }]
    }
  },
  career: {
    key: 'career', name: 'Career & Success', icon: '💼', color: '#3b82f6',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Authority', color: '#FFD700' }, { symbol: '👑', label: 'Leadership', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Public', color: '#C8C8FF' }, { symbol: '🌊', label: 'Emotions', color: '#4169E1' }],
      Mars: [{ symbol: '♂', label: 'Action', color: '#DC143C' }, { symbol: '⚔', label: 'Competition', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Business', color: '#32CD32' }, { symbol: '📊', label: 'Analysis', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Growth', color: '#FFA500' }, { symbol: '📈', label: 'Expansion', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Arts', color: '#FF69B4' }, { symbol: '✨', label: 'Luxury', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Hard Work', color: '#8B4513' }, { symbol: '⏳', label: 'Patience', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Ambition', color: '#4B0082' }, { symbol: '🚀', label: 'Innovation', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Detachment', color: '#006400' }, { symbol: '🔮', label: 'Intuition', color: '#32CD32' }]
    }
  },
  education: {
    key: 'education', name: 'Education & Knowledge', icon: '📚', color: '#8b5cf6',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Knowledge', color: '#FFD700' }, { symbol: '💡', label: 'Wisdom', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Memory', color: '#C8C8FF' }, { symbol: '🧠', label: 'Mind', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Logic', color: '#DC143C' }, { symbol: '🎯', label: 'Focus', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Learning', color: '#32CD32' }, { symbol: '📝', label: 'Writing', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Higher Ed', color: '#FFA500' }, { symbol: '🎓', label: 'Degrees', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Arts', color: '#FF69B4' }, { symbol: '🎨', label: 'Creative', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Discipline', color: '#8B4513' }, { symbol: '📖', label: 'Research', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Foreign', color: '#4B0082' }, { symbol: '🌍', label: 'Abroad', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Spiritual', color: '#006400' }, { symbol: '🕉', label: 'Vedic', color: '#32CD32' }]
    }
  },
  wealth: {
    key: 'wealth', name: 'Wealth & Prosperity', icon: '💰', color: '#22c55e',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Gold', color: '#FFD700' }, { symbol: '👑', label: 'Status', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Silver', color: '#C8C8FF' }, { symbol: '💎', label: 'Gems', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Property', color: '#DC143C' }, { symbol: '🏠', label: 'Real Estate', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Trade', color: '#32CD32' }, { symbol: '📈', label: 'Stocks', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Fortune', color: '#FFA500' }, { symbol: '🍀', label: 'Luck', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Luxury', color: '#FF69B4' }, { symbol: '💍', label: 'Jewelry', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Savings', color: '#8B4513' }, { symbol: '🏦', label: 'Banking', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Foreign', color: '#4B0082' }, { symbol: '💱', label: 'Forex', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Loss', color: '#006400' }, { symbol: '🔻', label: 'Detach', color: '#32CD32' }]
    }
  },
  spiritual: {
    key: 'spiritual', name: 'Spirituality & Moksha', icon: '🕉️', color: '#a855f7',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Soul', color: '#FFD700' }, { symbol: '✨', label: 'Atma', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Mind', color: '#C8C8FF' }, { symbol: '🧘', label: 'Peace', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Tapas', color: '#DC143C' }, { symbol: '🔥', label: 'Penance', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Mantra', color: '#32CD32' }, { symbol: '📿', label: 'Japa', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Guru', color: '#FFA500' }, { symbol: '🙏', label: 'Blessing', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Bhakti', color: '#FF69B4' }, { symbol: '💕', label: 'Devotion', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Karma', color: '#8B4513' }, { symbol: '⚖', label: 'Justice', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Maya', color: '#4B0082' }, { symbol: '🌀', label: 'Illusion', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Moksha', color: '#006400' }, { symbol: '🕊', label: 'Liberation', color: '#32CD32' }]
    }
  },
  travel: {
    key: 'travel', name: 'Travel & Settlement', icon: '✈️', color: '#06b6d4',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'East', color: '#FFD700' }, { symbol: '🌅', label: 'Sunrise', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Abroad', color: '#C8C8FF' }, { symbol: '🌊', label: 'Overseas', color: '#4169E1' }],
      Mars: [{ symbol: '♂', label: 'South', color: '#DC143C' }, { symbol: '🏔', label: 'Adventure', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'North', color: '#32CD32' }, { symbol: '🚂', label: 'Journey', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Pilgrimage', color: '#FFA500' }, { symbol: '🛕', label: 'Temple', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Luxury', color: '#FF69B4' }, { symbol: '🏝', label: 'Resort', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'West', color: '#8B4513' }, { symbol: '🌄', label: 'Sunset', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Foreign', color: '#4B0082' }, { symbol: '✈', label: 'Flight', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Return', color: '#006400' }, { symbol: '🏠', label: 'Home', color: '#32CD32' }]
    }
  },
  family: {
    key: 'family', name: 'Family & Children', icon: '👨‍👩‍👧', color: '#f472b6',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Father', color: '#FFD700' }, { symbol: '👨', label: 'Pitru', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Mother', color: '#C8C8FF' }, { symbol: '👩', label: 'Matru', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Sibling', color: '#DC143C' }, { symbol: '👫', label: 'Brother', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Cousins', color: '#32CD32' }, { symbol: '👥', label: 'Relatives', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Children', color: '#FFA500' }, { symbol: '👶', label: 'Santan', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Spouse', color: '#FF69B4' }, { symbol: '💑', label: 'Partner', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Elders', color: '#8B4513' }, { symbol: '👴', label: 'Ancestors', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'In-laws', color: '#4B0082' }, { symbol: '🏠', label: 'Extended', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Separation', color: '#006400' }, { symbol: '💔', label: 'Distance', color: '#32CD32' }]
    }
  },
  marriage: {
    key: 'marriage', name: 'Marriage & Love', icon: '💍', color: '#ec4899',
    astroElements: {
      Sun: [{ symbol: '☉', label: 'Ego', color: '#FFD700' }, { symbol: '👑', label: 'Pride', color: '#FFA500' }],
      Moon: [{ symbol: '☽', label: 'Emotion', color: '#C8C8FF' }, { symbol: '💕', label: 'Love', color: '#9370DB' }],
      Mars: [{ symbol: '♂', label: 'Passion', color: '#DC143C' }, { symbol: '🔥', label: 'Desire', color: '#FF4500' }],
      Mercury: [{ symbol: '☿', label: 'Commun', color: '#32CD32' }, { symbol: '💬', label: 'Talk', color: '#4682B4' }],
      Jupiter: [{ symbol: '♃', label: 'Husband', color: '#FFA500' }, { symbol: '🤵', label: 'Pati', color: '#32CD32' }],
      Venus: [{ symbol: '♀', label: 'Wife', color: '#FF69B4' }, { symbol: '👰', label: 'Patni', color: '#FFB6C1' }],
      Saturn: [{ symbol: '♄', label: 'Delay', color: '#8B4513' }, { symbol: '⏳', label: 'Waiting', color: '#A0522D' }],
      Rahu: [{ symbol: '☊', label: 'Unconven', color: '#4B0082' }, { symbol: '💫', label: 'Unusual', color: '#9400D3' }],
      Ketu: [{ symbol: '☋', label: 'Detach', color: '#006400' }, { symbol: '🕊', label: 'Freedom', color: '#32CD32' }]
    }
  }
};

// Planet icons and colors
const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

const DomainReelsStory = () => {
  const navigate = useNavigate();
  const { domainId } = useParams();
  
  // Get current domain from URL - support both key names and numbers
  const getDomainKey = () => {
    if (!domainId) return 'health';
    // Map numbers to domain keys - matches MyStoryTab.jsx order
    const numberMap = { 
      '1': 'health', 
      '2': 'career', 
      '3': 'wealth',      // MyStoryTab position 3 = Wealth
      '4': 'marriage',    // MyStoryTab position 4 = Marriage/Relationships
      '5': 'family',      // MyStoryTab position 5 = Family
      '6': 'education',   // MyStoryTab position 6 = Education
      '7': 'travel',      // MyStoryTab position 7 = Travel
      '8': 'spiritual'    // MyStoryTab position 8 = Spirituality
    };
    return numberMap[domainId] || domainId;
  };
  
  const currentDomainKey = getDomainKey();
  const currentDomainIndex = DOMAIN_ORDER.indexOf(currentDomainKey);
  const currentDomain = DOMAINS[currentDomainKey] || DOMAINS.health;
  
  // Get premium status from SessionContext (MongoDB is source of truth)
  const { isPremium } = useSession();
  
  // Check if data already exists - if so, skip ALL loading states
  const hasStoredData = typeof window !== 'undefined' && localStorage.getItem('unified_story_data');
  const [isLoading, setIsLoading] = useState(false); // Default false - check data first
  // ONLY use isPremium from SessionContext for lock status - NOT localStorage
  const [isLocked, setIsLocked] = useState(true); // Default to locked, will update from SessionContext
  const [isNarrationReady, setIsNarrationReady] = useState(hasStoredData); // Ready if data exists
  const [narrationLoadingProgress, setNarrationLoadingProgress] = useState(hasStoredData ? 100 : 0);
  const [currentPhase, setCurrentPhase] = useState('ready');
  const [selectedYear, setSelectedYear] = useState(-1);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [isPlaying, setIsPlaying] = useState(false); // Single play state like PastStory
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [currentNarrationText, setCurrentNarrationText] = useState('');
  const [displayedText, setDisplayedText] = useState('');
  const [storyComplete, setStoryComplete] = useState(false); // Track if story is complete
  const [swipeDirection, setSwipeDirection] = useState(null);
  
  // Per-language playback position tracking (like PastStoryRevolutionary)
  const [playbackPosition, setPlaybackPosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  const [audioTimePosition, setAudioTimePosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  
  const [futureMahadashas, setFutureMahadashas] = useState([]);
  const [personName, setPersonName] = useState('');
  const [introText, setIntroText] = useState('');
  const [timelineNarrations, setTimelineNarrations] = useState([]);
  
  const audioRef = useRef(null);
  const audioCache = useRef({ intro: {}, hinglish: {}, english: {}, hindi: {} });
  const abortRef = useRef(false); // Rename to match PastStory
  const textRevealIntervalRef = useRef(null);
  const currentLanguageRef = useRef('hinglish'); // Track current language for cleanup
  const audioUrlCacheRef = useRef({ hindi: {}, hinglish: {}, english: {} }); // URL cache like PastStory
  
  // Store all language intros and narrations for language switching
  const allIntrosRef = useRef({});
  const allTimelineNarrationsRef = useRef({});

  // SAVE current domain position to localStorage (for swipe/navigation)
  const saveDomainPosition = useCallback(() => {
    const currentTime = audioRef.current?.currentTime || 0;
    const positionData = {
      domain: currentDomainKey,
      language: selectedLanguage,
      sectionIndex: currentSectionIndex,
      audioTime: currentTime,
      playbackPos: playbackPosition,
      audioTimePos: audioTimePosition,
      timestamp: Date.now()
    };
    
    // Save to localStorage keyed by domain
    const allPositions = JSON.parse(localStorage.getItem('domain_reel_positions') || '{}');
    allPositions[currentDomainKey] = positionData;
    localStorage.setItem('domain_reel_positions', JSON.stringify(allPositions));
    
    console.log(`💾 Saved ${currentDomainKey} position: lang=${selectedLanguage}, section=${currentSectionIndex}, time=${currentTime.toFixed(2)}s`);
  }, [currentDomainKey, selectedLanguage, currentSectionIndex, playbackPosition, audioTimePosition]);

  // RESTORE domain position from localStorage
  const restoreDomainPosition = useCallback(() => {
    const allPositions = JSON.parse(localStorage.getItem('domain_reel_positions') || '{}');
    const savedPosition = allPositions[currentDomainKey];
    
    if (savedPosition && savedPosition.domain === currentDomainKey) {
      // Check if position is recent (within 1 hour)
      const isRecent = (Date.now() - savedPosition.timestamp) < 3600000;
      
      if (isRecent) {
        // Restore positions
        setSelectedLanguage(savedPosition.language || 'hinglish');
        currentLanguageRef.current = savedPosition.language || 'hinglish';
        setCurrentSectionIndex(savedPosition.sectionIndex || 0);
        setPlaybackPosition(savedPosition.playbackPos || { hindi: 0, hinglish: 0, english: 0 });
        setAudioTimePosition(savedPosition.audioTimePos || { hindi: 0, hinglish: 0, english: 0 });
        
        console.log(`📍 Restored ${currentDomainKey} position: lang=${savedPosition.language}, section=${savedPosition.sectionIndex}, time=${savedPosition.audioTime?.toFixed(2)}s`);
        return true;
      }
    }
    return false;
  }, [currentDomainKey]);

  // Clear audio cache when domain changes
  useEffect(() => {
    console.log(`🔄 Domain changed to: ${currentDomainKey}`);
    // Reset audio cache for new domain
    audioCache.current = { intro: {}, hinglish: {}, english: {}, hindi: {} };
    // Reset state for new domain
    setCurrentPhase('ready');
    setSelectedYear(-1);
    setDisplayedText('');
    setCurrentNarrationText('');
    setShowSkipButton(false);
    setIsPlaying(false);
    setStoryComplete(false);
    setIsNarrationReady(false); // Reset narration ready state
    setNarrationLoadingProgress(0);
    
    // Try to restore saved position for this domain
    restoreDomainPosition();
  }, [currentDomainKey, restoreDomainPosition]);

  // CRITICAL: Stop all audio completely - identical to PastStoryRevolutionary
  const stopAllAudio = useCallback(() => {
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current.load(); // Force release
      audioRef.current = null;
    }
    
    // Stop global audio reference
    if (window.currentDomainAudio) {
      window.currentDomainAudio.pause();
      window.currentDomainAudio.currentTime = 0;
      window.currentDomainAudio.src = '';
      window.currentDomainAudio = null;
    }
    
    // Clear any pending text intervals
    if (textRevealIntervalRef.current) {
      clearInterval(textRevealIntervalRef.current);
      textRevealIntervalRef.current = null;
    }
    
    // Set abort flag
    abortRef.current = true;
  }, []);

  // VISIBILITY CHANGE HANDLER - Pause when tab/app loses focus (identical to PastStory)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        console.log('👁️ Tab hidden - saving position and pausing');
        // Save current time position
        const currentTime = audioRef.current?.currentTime || 0;
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
        
        // SAVE domain position to localStorage when tab hidden
        saveDomainPosition();
        
        // Stop all audio
        stopAllAudio();
        setIsPlaying(false);
      }
    };
    
    const handleBlur = () => {
      if (isPlaying) {
        console.log('🔇 Window blur - saving position and pausing');
        // Save current time position
        const currentTime = audioRef.current?.currentTime || 0;
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
        
        // SAVE domain position to localStorage when window blurred
        saveDomainPosition();
        
        // Stop all audio
        stopAllAudio();
        setIsPlaying(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isPlaying, selectedLanguage, stopAllAudio, saveDomainPosition]);

  // Check if user has paid/unlocked - ONLY use isPremium from SessionContext
  useEffect(() => {
    // isPremium from SessionContext is the ONLY source of truth
    setIsLocked(!isPremium);
    console.log('🔒 Future Reel Lock Status:', !isPremium ? 'LOCKED' : 'UNLOCKED (Premium)', { isPremium });
  }, [isPremium]);

  // Cleanup on unmount - use stopAllAudio
  // Only cleanup if not actively playing (prevents Strict Mode issues)
  const isPlayingRef = useRef(false);
  
  useEffect(() => {
    return () => {
      // Only log and cleanup if NOT actively playing
      if (!isPlayingRef.current) {
        console.log('🧹 Component unmounting - cleaning up audio');
        saveDomainPosition();
        stopAllAudio();
      } else {
        console.log('⏸️ Skipping cleanup - audio is playing');
      }
    };
  }, [stopAllAudio, saveDomainPosition]);

  // Flag to track if audio should be cancelled
  const audioAbortRef = useRef(false);

  // COMPLETE AUDIO CLEANUP FUNCTION - Uses stopAllAudio and resets state
  const completeAudioCleanup = useCallback(() => {
    console.log('🛑 Complete audio cleanup triggered');
    stopAllAudio();
    isPlayingRef.current = false; // Mark as not playing
    
    // Reset all state
    setIsPlaying(false);
    setStoryComplete(false);
    setCurrentPhase('ready');
    setSelectedYear(-1);
    setShowSkipButton(false);
    setDisplayedText('');
    setCurrentNarrationText('');
    
    // Reset abort flag after a short delay
    setTimeout(() => {
      abortRef.current = false;
    }, 100);
  }, [stopAllAudio]);

  // Stop text reveal - defined as useCallback so it can be used in other callbacks
  const stopTextReveal = useCallback(() => {
    if (textRevealIntervalRef.current) {
      clearInterval(textRevealIntervalRef.current);
      textRevealIntervalRef.current = null;
    }
  }, []);

  // Navigate to adjacent domain
  const navigateToDomain = useCallback((direction) => {
    const newIndex = currentDomainIndex + direction;
    if (newIndex >= 0 && newIndex < DOMAIN_ORDER.length) {
      console.log(`🔄 Navigating from ${currentDomainKey} to domain index ${newIndex}`);
      
      // SAVE current domain position before switching
      saveDomainPosition();
      
      // FULLY STOP narration before switching
      completeAudioCleanup();
      
      // Clear all state for fresh start
      setFutureMahadashas([]);
      setTimelineNarrations([]);
      setIntroText('');
      setCurrentPhase('ready');
      setSelectedYear(-1);
      
      setSwipeDirection(direction > 0 ? 'left' : 'right');
      
      // Navigate to new domain
      const newDomain = DOMAIN_ORDER[newIndex];
      setTimeout(() => {
        navigate(`/domain-reels/${newDomain}`, { replace: true });
      }, 100);
    }
  }, [currentDomainIndex, currentDomainKey, navigate, completeAudioCleanup, saveDomainPosition]);

  // Swipe handlers for domain navigation
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => navigateToDomain(1),  // Next domain
    onSwipedRight: () => navigateToDomain(-1), // Previous domain
    preventScrollOnSwipe: true,
    trackMouse: false, // DISABLED - was causing auto-navigation issues
    delta: 100, // Increased swipe threshold
    swipeDuration: 500 // Require longer swipe
  });

  // Dynamic text reveal - SYNCED with audio, TELEPROMPTER style (old text fades out)
  // Update displayed text - SAME as Past Story (immediate, no progressive reveal)
  const previousTextRef = useRef('');
  const isUpdatingTextRef = useRef(false);
  
  const updateDisplayedText = useCallback((newText) => {
    // Skip if same text or currently updating
    if (newText === previousTextRef.current || isUpdatingTextRef.current) return;
    
    isUpdatingTextRef.current = true;
    previousTextRef.current = newText;
    
    // Direct update - no setTimeout to avoid flicker (SAME AS PAST STORY)
    setDisplayedText(newText);
    
    // Reset flag after a short delay
    setTimeout(() => {
      isUpdatingTextRef.current = false;
    }, 100);
  }, []);

  // Legacy function for compatibility - just calls updateDisplayedText
  const startTextReveal = (fullText, durationMs) => {
    // Stop any existing reveal
    if (textRevealIntervalRef.current) {
      clearInterval(textRevealIntervalRef.current);
      textRevealIntervalRef.current = null;
    }
    
    // Clean the text first
    const cleanText = fullText.trim();
    if (!cleanText) {
      setDisplayedText('');
      return;
    }
    
    // SAME AS PAST STORY: Show full text immediately, no word-by-word reveal
    updateDisplayedText(cleanText);
  };

  // Helper to remove emojis from text - keep only planet symbols and clean text
  const cleanNarrationText = (text) => {
    if (!text) return '';
    // Remove emojis like ❤️💼📚💰🎯⚠️🏥💪💡🙏 etc but keep planet symbols ☉☽♂☿♃♀♄☊☋
    return text
      .replace(/[❤️💼📚💰🎯⚠️🏥💪💡🙏🕉️✈️👨‍👩‍👧💍🌊🏔️🛕🏝️🌄🏠💔🔥🕊️🌀⚖️💕💫🎨🎓📖📿🚀🔮⏳👑🍀💎🏦💱🔻🧘✨🎬]/g, '')
      // Remove awkward headers - ALL PATTERNS
      // Pattern: "NAME — FUTURE HEALTH & LONGEVITY STORY (THE COMPLETE MOVIE)"
      .replace(/[A-Z][A-Za-z\s]+[—–-]\s*FUTURE\s+[A-Z\s&]+STORY[^\n]*/gi, '')
      // Pattern: "Name - Future Health Story Part 1"
      .replace(/[A-Za-z\s]+[—–-]\s*Future\s+[A-Za-z\s&]+Story[^\n]*/gi, '')
      // Pattern: "PART 1 — OVERALL FUTURE HEALTH STORY"
      .replace(/PART\s*[12]\s*[—–-][^\n]*/gi, '')
      // Pattern: "Part 1" or "Part 2" alone
      .replace(/\bPart\s*[12]\b/gi, '')
      // Hindi patterns
      .replace(/[A-Z\s]+—\s*भविष्य[^\n]+\(संपूर्ण फिल्म\)/gi, '')
      .replace(/भाग\s*[12]\s*[—–-][^\n]*/gi, '')
      // Pattern: "(THE COMPLETE MOVIE)" or "(Complete Movie)"
      .replace(/\(THE COMPLETE MOVIE\)/gi, '')
      .replace(/\(Complete Movie\)/gi, '')
      .replace(/\(संपूर्ण फिल्म\)/gi, '')
      // Clean up multiple newlines and spaces
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Generate domain-specific narration for a mahadasha - PROPER LANGUAGE-SPECIFIC (no emojis, no repetition)
  const generateDomainNarration = (md, lang = 'hinglish') => {
    const domainPred = md.domain_predictions?.[currentDomainKey] || {};
    const summary = domainPred.summary || domainPred.detailed_summary || '';
    const advice = domainPred.advice || '';
    const focusAreas = (domainPred.focus_areas || []).slice(0, 3);
    const diseaseRisks = (domainPred.disease_risks || []).slice(0, 2);
    const remedies = (domainPred.remedies || []).slice(0, 2);
    const surgeryRisk = domainPred.surgery_risk;
    const vitality = domainPred.vitality;
    const level = domainPred.level || 'moderate';
    
    const lordName = md.lord || 'Unknown';
    const years = md.years || '';
    
    // Build LANGUAGE-SPECIFIC narration - NO REPEATED PHRASES
    let narration = '';
    
    if (lang === 'hindi') {
      // PURE HINDI narration - Generate Hindi description instead of using English summary
      narration = `${lordName} महादशा, ${years}।\n\n`;
      
      // Generate Hindi description based on level
      const levelDesc = {
        'excellent': 'यह समय बहुत शुभ रहेगा।',
        'favorable': 'यह समय अनुकूल रहेगा।',
        'good': 'अच्छे परिणाम मिलेंगे।',
        'moderate': 'सामान्य समय रहेगा।',
        'careful': 'सावधानी से चलें।',
        'challenging': 'यह समय चुनौतीपूर्ण रहेगा।'
      };
      narration += `${levelDesc[level] || levelDesc.moderate}\n\n`;
      
      // Add domain-specific Hindi content
      if (currentDomainKey === 'health') {
        narration += `स्वास्थ्य के लिए ${lordName} का प्रभाव ${level === 'favorable' || level === 'excellent' ? 'अच्छा' : 'मिश्रित'} रहेगा।\n`;
        if (diseaseRisks.length > 0) {
          narration += `ध्यान रखें: ${diseaseRisks.join(', ')}।\n`;
        }
        if (surgeryRisk) {
          narration += `चिकित्सा उपचार की संभावना है।\n`;
        }
      } else if (currentDomainKey === 'career') {
        narration += `करियर में ${level === 'favorable' || level === 'excellent' ? 'उन्नति' : 'स्थिरता'} का समय।\n`;
      } else if (currentDomainKey === 'wealth') {
        narration += `आर्थिक स्थिति ${level === 'favorable' || level === 'excellent' ? 'मजबूत' : 'सामान्य'} रहेगी।\n`;
      } else if (currentDomainKey === 'education') {
        narration += `शिक्षा और ज्ञान में ${level === 'favorable' || level === 'excellent' ? 'प्रगति' : 'निरंतरता'} रहेगी।\n`;
      } else if (currentDomainKey === 'marriage') {
        narration += `वैवाहिक जीवन ${level === 'favorable' || level === 'excellent' ? 'सुखमय' : 'सामान्य'} रहेगा।\n`;
      } else if (currentDomainKey === 'family') {
        narration += `पारिवारिक संबंध ${level === 'favorable' || level === 'excellent' ? 'मधुर' : 'स्थिर'} रहेंगे।\n`;
      } else if (currentDomainKey === 'travel') {
        narration += `यात्रा के ${level === 'favorable' || level === 'excellent' ? 'अच्छे अवसर' : 'सीमित अवसर'} मिलेंगे।\n`;
      } else if (currentDomainKey === 'spiritual') {
        narration += `आध्यात्मिक विकास ${level === 'favorable' || level === 'excellent' ? 'उत्कृष्ट' : 'सामान्य'} रहेगा।\n`;
      }
      
      if (focusAreas.length > 0) {
        narration += `ध्यान दें: ${focusAreas.join(', ')}।\n`;
      }
      if (remedies.length > 0) {
        narration += `उपाय: ${remedies.join(', ')}।`;
      }
      
    } else if (lang === 'english') {
      // PURE ENGLISH narration
      narration = `${lordName} Mahadasha, ${years}.\n\n`;
      
      if (summary) {
        narration += `${summary}\n\n`;
      }
      
      if (focusAreas.length > 0) {
        narration += `Focus on: ${focusAreas.join(', ')}.\n`;
      }
      
      if (currentDomainKey === 'health') {
        if (diseaseRisks.length > 0) {
          narration += `Health caution: ${diseaseRisks.join(', ')}.\n`;
        }
        if (surgeryRisk) {
          narration += `Surgical procedures may be needed. Take precautions.\n`;
        }
        if (vitality) {
          narration += `Energy levels: Physical ${vitality.physical || 'moderate'}, Mental ${vitality.mental || 'stable'}.\n`;
        }
      }
      
      if (advice) {
        narration += `\nGuidance: ${advice}`;
      }
      if (remedies.length > 0) {
        narration += `\nRemedies: ${remedies.join(', ')}`;
      }
      
    } else {
      // HINGLISH - Proper romanized Hindi (NOT English content, romanized Hindi descriptions)
      narration = `${lordName} ki Mahadasha, ${years}.\n\n`;
      
      // Generate Hinglish description based on level - DON'T use English summary
      const levelDescHinglish = {
        'excellent': 'Yeh samay bahut achha rahega.',
        'favorable': 'Yeh samay anukool rahega.',
        'good': 'Achhe nataaje milenge.',
        'moderate': 'Saamanya samay rahega.',
        'careful': 'Savdhani se chalein.',
        'challenging': 'Yeh samay chunautipurn rahega.'
      };
      narration += `${levelDescHinglish[level] || levelDescHinglish.moderate}\n\n`;
      
      // Add domain-specific Hinglish content
      if (currentDomainKey === 'health') {
        narration += `Sehat ke liye ${lordName} ka prabhav ${level === 'favorable' || level === 'excellent' ? 'achha' : 'mishra'} rahega.\n`;
        if (diseaseRisks.length > 0) {
          narration += `Savdhani: ${diseaseRisks.join(', ')}.\n`;
        }
        if (surgeryRisk) {
          narration += `Surgery ki sambhavna hai. Dhyan rakhein.\n`;
        }
      } else if (currentDomainKey === 'career') {
        narration += `Career mein ${level === 'favorable' || level === 'excellent' ? 'tarakki' : 'sthirata'} ka samay hai.\n`;
      } else if (currentDomainKey === 'wealth') {
        narration += `Aarthik sthiti ${level === 'favorable' || level === 'excellent' ? 'mazboot' : 'saamanya'} rahegi.\n`;
      } else if (currentDomainKey === 'education') {
        narration += `Shiksha mein ${level === 'favorable' || level === 'excellent' ? 'pragati' : 'nirantarta'} rahegi.\n`;
      } else if (currentDomainKey === 'marriage') {
        narration += `Vaivaahik jeevan ${level === 'favorable' || level === 'excellent' ? 'sukhmay' : 'saamanya'} rahega.\n`;
      } else if (currentDomainKey === 'family') {
        narration += `Parivarik sambandh ${level === 'favorable' || level === 'excellent' ? 'madhur' : 'sthir'} rahenge.\n`;
      } else if (currentDomainKey === 'travel') {
        narration += `Yatra ke ${level === 'favorable' || level === 'excellent' ? 'achhe avsar' : 'seemit avsar'} milenge.\n`;
      } else if (currentDomainKey === 'spiritual') {
        narration += `Aadhyatmik vikas ${level === 'favorable' || level === 'excellent' ? 'uttam' : 'saamanya'} rahega.\n`;
      }
      
      if (focusAreas.length > 0) {
        narration += `Is waqt ${focusAreas.join(', ')} par dhyan dein.\n`;
      }
      if (remedies.length > 0) {
        narration += `Upay: ${remedies.join(', ')}`;
      }
    }
    
    // Fallback if narration is too short
    if (narration.length < 80) {
      if (lang === 'hindi') {
        narration += `\n\n${lordName} ग्रह आपके ${currentDomain.name} को प्रभावित करेगा।`;
      } else if (lang === 'english') {
        narration += `\n\nThe ${lordName} planet will influence your ${currentDomain.name}.`;
      } else {
        narration += `\n\n${lordName} graha aapke ${currentDomain.name} ko influence karega.`;
      }
    }
    
    return narration.trim();
  };

  // Load domain-specific future story data - LOAD FROM STORAGE ONLY (like PastStory)
  useEffect(() => {
    const loadData = async () => {
      // QUICK PATH: If unified_story_data exists with sections, skip loading entirely
      const storedData = localStorage.getItem('unified_story_data');
      if (storedData) {
        try {
          const unifiedStoryData = JSON.parse(storedData);
          const domainReelsData = unifiedStoryData.future_story?.domain_reels || {};
          const isMultiLang = domainReelsData.hinglish || domainReelsData.english || domainReelsData.hindi;
          
          // Check if we have content for this domain
          const hasContent = isMultiLang 
            ? (domainReelsData.hinglish?.[currentDomainKey] || domainReelsData.english?.[currentDomainKey])
            : domainReelsData[currentDomainKey];
          
          if (hasContent) {
            console.log(`⚡ Fast path: ${currentDomainKey} data already exists`);
            // Don't show loading at all - data is ready
            setIsLoading(false);
            setIsNarrationReady(true);
          }
        } catch (e) {
          console.log('Quick data check failed:', e.message);
        }
      }
      
      setIsLoading(true);
      setIsNarrationReady(false);
      
      // QUICK CHECK: Try to load batch audio first (if already generated)
      // This avoids showing "Loading..." when audio is already cached
      const quickUserId = getGenerationUserId();
      if (quickUserId) {
        try {
          const batchResult = await loadBatchAudio();
          if (batchResult?.audioLoaded && batchResult?.texts) {
            console.log(`✅ Quick load: ${currentDomainKey} batch audio ready!`);
            
            // Get story data for mahadashas and other info
            const storedData = localStorage.getItem('unified_story_data');
            if (storedData) {
              const unifiedStoryData = JSON.parse(storedData);
              
              // Load future mahadashas
              const futureMD = unifiedStoryData.future_story?.mahadashas || [];
              const filteredMD = futureMD.filter(md => md.status === 'current' || md.status === 'future');
              setFutureMahadashas(filteredMD);
              console.log(`✅ Quick load: ${filteredMD.length} mahadashas loaded`);
              
              // Set birth data
              const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
              if (birthDetails) {
                const birthData = JSON.parse(birthDetails);
                setPersonName(birthData.name || 'User');
              }
            }
            
            // Set text from batch - but DON'T show initially
            const primaryText = batchResult.texts[selectedLanguage] || batchResult.texts.hinglish || batchResult.texts.english || batchResult.texts.hindi || '';
            setIntroText(primaryText);
            // DON'T set displayedText yet - wait for play button
            // setDisplayedText(primaryText);
            setTimelineNarrations([primaryText]);
            
            allTimelineNarrationsRef.current = {
              hinglish: batchResult.texts.hinglish ? [batchResult.texts.hinglish] : [],
              english: batchResult.texts.english ? [batchResult.texts.english] : [],
              hindi: batchResult.texts.hindi ? [batchResult.texts.hindi] : []
            };
            allIntrosRef.current = {
              hinglish: batchResult.texts.hinglish || '',
              english: batchResult.texts.english || '',
              hindi: batchResult.texts.hindi || ''
            };
            
            setIsNarrationReady(true);
            setIsLoading(false);
            return; // Skip the rest of loading
          }
        } catch (e) {
          console.log('Quick batch check failed, continuing normal load:', e.message);
        }
      }
      
      // Get birth details
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (!birthDetails) {
        console.log('❌ No birth details found for Future Reel');
        navigate('/onboarding-new');
        return;
      }
      
      let birthData;
      try {
        birthData = JSON.parse(birthDetails);
      } catch (e) {
        console.error('Error parsing birth details:', e);
        navigate('/onboarding-new');
        return;
      }
      
      // Use consistent user ID - MUST match TTS generation user ID
      const userId = getGenerationUserId();
      const userName = birthData.name || 'User';
      setPersonName(userName);
      
      console.log(`🔍 Loading story reels for user: ${userId}`);
      
      // STEP 1: Try to load unified story from localStorage (already generated by LoadingStoryGenerator)
      let unifiedStoryData = null;
      const storedDataStep1 = localStorage.getItem('unified_story_data');
      
      if (storedDataStep1) {
        try {
          unifiedStoryData = JSON.parse(storedDataStep1);
          console.log(`✅ Loaded unified story from localStorage for ${userName}`);
        } catch (e) {
          console.log('Failed to parse stored data');
        }
      }
      
      // STEP 1B: Try AI content data (newer, AI-generated content)
      let aiContentData = null;
      const aiStored = localStorage.getItem('ai_content_data');
      if (aiStored) {
        try {
          aiContentData = JSON.parse(aiStored);
          console.log('✅ AI content loaded for Domain Reels:', Object.keys(aiContentData.future_reels || {}));
        } catch (e) {
          console.log('Failed to parse AI content');
        }
      }
      
      // STEP 2: If not in localStorage, try MongoDB with consistent user ID
      if (!unifiedStoryData && userId) {
        try {
          console.log(`📡 Fetching story from MongoDB for user: ${userId}`);
          const response = await fetch(`${API_URL}/api/unified-story/load/${userId}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              unifiedStoryData = result.data;
              // IMPORTANT: Also save to localStorage for future quick access
              localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData));
              console.log(`✅ Loaded unified story from MongoDB for ${userName} and saved to localStorage`);
            } else {
              console.log(`⚠️ MongoDB returned no data: ${result.message || 'No story found'}`);
            }
          } else {
            console.log(`⚠️ MongoDB fetch failed: ${response.status}`);
          }
        } catch (e) {
          console.log('MongoDB load error:', e.message);
        }
      }
      
      // STEP 2B: Merge AI content into unified story data if available
      if (aiContentData?.future_reels) {
        if (!unifiedStoryData) {
          unifiedStoryData = { user_info: aiContentData.user_info || { name: userName } };
        }
        
        // Inject AI future reels into domain_reels structure
        const aiReels = aiContentData.future_reels;
        if (!unifiedStoryData.future_story) {
          unifiedStoryData.future_story = { domain_reels: {} };
        }
        
        // AI reels now come in language-first structure: future_reels.english.health
        // OR old format: future_reels.health (single language)
        for (const lang of ['english', 'hindi', 'hinglish']) {
          if (!unifiedStoryData.future_story.domain_reels[lang]) {
            unifiedStoryData.future_story.domain_reels[lang] = {};
          }
          
          // Check if new format (language-first)
          if (aiReels[lang] && typeof aiReels[lang] === 'object') {
            // New format: future_reels.english.health
            for (const [domain, content] of Object.entries(aiReels[lang])) {
              if (content && typeof content === 'string') {
                unifiedStoryData.future_story.domain_reels[lang][domain] = content;
              }
            }
          } else {
            // Old format: future_reels.health (use same content for all languages)
            for (const [domain, content] of Object.entries(aiReels)) {
              if (domain !== '_tokens' && domain !== '_language' && content && typeof content === 'string') {
                unifiedStoryData.future_story.domain_reels[lang][domain] = content;
              }
            }
          }
        }
        
        console.log('✅ AI future reels merged into story data');
      }
      
      // STEP 3: If still no data, redirect to loading screen (should generate there)
      if (!unifiedStoryData) {
        console.log('❌ No story data found for Future Reel, redirecting to loading screen');
        navigate('/generating-story');
        return;
      }
      
      // Get future mahadashas (current + future periods)
      const futureMds = unifiedStoryData.future_story?.mahadashas || 
                       unifiedStoryData.all_mahadashas?.filter(m => m.status === 'current' || m.status === 'future') || [];
      setFutureMahadashas(futureMds);
      
      console.log(`📊 Future Mahadashas for ${currentDomain.name}: ${futureMds.length}`);
      futureMds.slice(0, 3).forEach((md, i) => console.log(`  ${i+1}. ${md.lord}: ${md.years}`));
      
      // ==== STEP 4: CHECK FOR PRE-LOADED AUDIO FROM LoadingDomainReel ====
      const cacheKey = `domain_audio_cache_${currentDomainKey}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          // Check if cache is recent (within 1 hour)
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < 3600000) {
            console.log(`✅ Using pre-loaded audio cache for ${currentDomainKey}`);
            
            // Store audio URLs in refs
            if (parsed.audioUrls) {
              audioUrlCacheRef.current = parsed.audioUrls;
            }
            
            // Store chunks as sections
            if (parsed.chunks) {
              const hinglishChunks = parsed.chunks.hinglish || [];
              const englishChunks = parsed.chunks.english || [];
              const hindiChunks = parsed.chunks.hindi || [];
              
              setTimelineNarrations(hinglishChunks);
              allTimelineNarrationsRef.current = {
                hinglish: hinglishChunks,
                english: englishChunks,
                hindi: hindiChunks
              };
              
              const introSection = hinglishChunks[0] || '';
              setIntroText(introSection);
              allIntrosRef.current = {
                hinglish: hinglishChunks[0] || '',
                english: englishChunks[0] || '',
                hindi: hindiChunks[0] || ''
              };
              
              console.log(`📝 Loaded from cache - Hinglish: ${hinglishChunks.length}, English: ${englishChunks.length}, Hindi: ${hindiChunks.length}`);
              
              setIsNarrationReady(true);
              setIsLoading(false);
              return;
            }
          } else {
            // Cache expired, remove it
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.log('Cache parse error:', e.message);
          localStorage.removeItem(cacheKey);
        }
      }
      
      // ==== STEP 5: LOAD DOMAIN-SPECIFIC AUDIO FROM MONGODB ====
      console.log(`📥 Loading ${currentDomainKey} audio from MongoDB...`);
      const loadedData = await loadAudioFromMongoDB();
      
      // If audio loaded successfully with sections
      if (loadedData && loadedData.sections && loadedData.sections.length > 0) {
        console.log(`✅ ${currentDomainKey} ready with pre-loaded audio (${loadedData.sections.length} sections)`);
        
        // Store sections for ALL languages
        const sections = loadedData.sections;
        setTimelineNarrations(sections);
        
        // If we have batch texts, use them for each language
        if (loadedData.batchTexts) {
          allTimelineNarrationsRef.current = {
            hinglish: loadedData.batchTexts.hinglish ? [loadedData.batchTexts.hinglish] : sections,
            english: loadedData.batchTexts.english ? [loadedData.batchTexts.english] : sections,
            hindi: loadedData.batchTexts.hindi ? [loadedData.batchTexts.hindi] : sections
          };
          
          // Set intro text based on selected language
          const introForLang = loadedData.batchTexts[selectedLanguage] || sections[0] || '';
          setIntroText(introForLang);
          allIntrosRef.current = {
            hinglish: loadedData.batchTexts.hinglish || sections[0] || '',
            english: loadedData.batchTexts.english || sections[0] || '',
            hindi: loadedData.batchTexts.hindi || sections[0] || ''
          };
          
          // DON'T set displayed text - wait for user to click play
          // setDisplayedText(introForLang);
        } else {
          // Store in refs for language switching - sections are same text
          allTimelineNarrationsRef.current = {
            hinglish: sections,
            english: sections,
            hindi: sections
          };
          
          // Use first section as intro
          const introSection = sections[0] || '';
          setIntroText(introSection);
          allIntrosRef.current = {
            hinglish: introSection,
            english: introSection,
            hindi: introSection
          };
          
          // DON'T set displayed text - wait for user to click play
          // setDisplayedText(introSection);
        }
        
        setIsNarrationReady(true);
        setIsLoading(false);
        return;
      }
      
      // FALLBACK: Use domain_reels from unified data if no cache or MongoDB data
      console.log(`⚠️ No pre-loaded audio for ${currentDomainKey}, using domain_reels fallback`);
      const domainReelsData = unifiedStoryData.future_story?.domain_reels || {};
      
      // Debug log to show available domains
      console.log('📦 domain_reels structure:', Object.keys(domainReelsData));
      if (domainReelsData.hinglish) {
        console.log('   Hinglish domains:', Object.keys(domainReelsData.hinglish));
      }
      if (domainReelsData.english) {
        console.log('   English domains:', Object.keys(domainReelsData.english));
      }
      if (domainReelsData.hindi) {
        console.log('   Hindi domains:', Object.keys(domainReelsData.hindi));
      }
      
      // NEW STRUCTURE: domain_reels[language][domain] for multi-language support
      // OLD STRUCTURE: domain_reels[domain] (fallback for old data)
      const isMultiLang = domainReelsData.hinglish || domainReelsData.english || domainReelsData.hindi;
      
      // Helper to get reel for a specific language
      const getReelForLang = (lang) => {
        if (isMultiLang) {
          return domainReelsData[lang]?.[currentDomainKey] || '';
        } else {
          // Old structure - same content for all languages
          return domainReelsData[currentDomainKey] || '';
        }
      };
      
      // Split reel into sections helper
      const splitIntoSections = (reelText) => {
        if (!reelText || reelText.length < 30) return []; // Reduced minimum for testing
        
        // Remove ALL awkward headers that shouldn't be narrated
        let cleanedReel = reelText
          // Remove emojis
          .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏|🎬/gu, '')
          // Remove "NAME — FUTURE HEALTH & LONGEVITY STORY (THE COMPLETE MOVIE)"
          .replace(/[A-Z][A-Za-z\s]+[—–-]\s*FUTURE\s+[A-Z\s&]+STORY[^\n]*/gi, '')
          // Remove "Name - Future Health Story Part 1"
          .replace(/[A-Za-z\s]+[—–-]\s*Future\s+[A-Za-z\s&]+Story[^\n]*/gi, '')
          // Remove "PART 1 — ..." or "PART 2 — ..."
          .replace(/PART\s*[12]\s*[—–-][^\n]*/gi, '')
          // Remove standalone "Part 1" or "Part 2"
          .replace(/\bPart\s*[12]\b/gi, '')
          // Hindi patterns
          .replace(/[A-Z\s]+—\s*भविष्य[^\n]+/gi, '')
          .replace(/भाग\s*[12]\s*[—–-][^\n]*/gi, '')
          // Remove "(THE COMPLETE MOVIE)" etc
          .replace(/\(THE COMPLETE MOVIE\)/gi, '')
          .replace(/\(Complete Movie\)/gi, '')
          .replace(/\(संपूर्ण फिल्म\)/gi, '')
          // Clean up multiple newlines
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        const sections = [];
        let currentSection = '';
        const paragraphs = cleanedReel.split(/\n\n+/);
        
        for (const para of paragraphs) {
          if ((currentSection + para).length > 1800) {
            if (currentSection.length > 30) sections.push(currentSection.trim()); // Reduced minimum
            currentSection = para;
          } else {
            currentSection += (currentSection ? '\n\n' : '') + para;
          }
        }
        if (currentSection.length > 30) sections.push(currentSection.trim()); // Reduced minimum
        if (sections.length === 0 && cleanedReel.length > 30) sections.push(cleanedReel); // Reduced minimum
        
        return sections;
      };
      
      // Get sections for ALL 3 languages
      const hinglishSections = splitIntoSections(getReelForLang('hinglish'));
      const englishSections = splitIntoSections(getReelForLang('english'));
      const hindiSections = splitIntoSections(getReelForLang('hindi'));
      
      // Use current language's sections
      const currentSections = selectedLanguage === 'english' ? englishSections 
                            : selectedLanguage === 'hindi' ? hindiSections 
                            : hinglishSections;
      
      if (currentSections.length > 0) {
        console.log(`📝 Using domain_reel for ${currentDomain.name} in ${selectedLanguage} (${currentSections.length} sections)`);
        
        setTimelineNarrations(currentSections);
        setIntroText(currentSections[0]);
        
        // Store ALL language sections in refs for language switching
        allTimelineNarrationsRef.current = {
          hinglish: hinglishSections.length > 0 ? hinglishSections : currentSections,
          english: englishSections.length > 0 ? englishSections : currentSections,
          hindi: hindiSections.length > 0 ? hindiSections : currentSections
        };
        allIntrosRef.current = {
          hinglish: hinglishSections[0] || currentSections[0],
          english: englishSections[0] || currentSections[0],
          hindi: hindiSections[0] || currentSections[0]
        };
        
        console.log(`📝 Sections loaded - Hinglish: ${hinglishSections.length}, English: ${englishSections.length}, Hindi: ${hindiSections.length}`);
        
        // DON'T show text before play - wait for user to click play button
        // setDisplayedText(currentSections[0] || '');
      } else {
        console.log(`⚠️ No domain reel for ${currentDomain.name}, using fallback`);
        
        // Fallback intros for each language - Make them longer so TTS works
        const fallbackIntros = {
          hindi: `नमस्ते ${userName}! मैं आपको आपकी ${currentDomain.name} की पूरी कहानी सुनाऊंगा। दुर्भाग्य से, इस डोमेन के लिए विस्तृत कहानी अभी उपलब्ध नहीं है। कृपया अपनी कहानी को फिर से जनरेट करें।`,
          english: `Hello ${userName}! I will tell you about your ${currentDomain.name} future. Unfortunately, the detailed story for this domain is not available yet. Please regenerate your story to get the full ${currentDomain.name} narration.`,
          hinglish: `Namaste ${userName}! Main aapko aapki ${currentDomain.name} ki poori kahani sunaunga. Maaf kijiye, is domain ki detailed story abhi available nahi hai. Please apni story regenerate karein.`
        };
        
        setIntroText(fallbackIntros[selectedLanguage]);
        // DON'T show fallback text until play - only show if there's really nothing
        // setDisplayedText(fallbackIntros[selectedLanguage]);
        allIntrosRef.current = fallbackIntros;
        
        // Set fallback as the timeline narration so it plays
        setTimelineNarrations([fallbackIntros[selectedLanguage]]);
        allTimelineNarrationsRef.current = { 
          hinglish: [fallbackIntros.hinglish], 
          english: [fallbackIntros.english], 
          hindi: [fallbackIntros.hindi] 
        };
        
        console.log(`⚠️ Using fallback narration for ${currentDomain.name}`);
      }
      
      // Audio not pre-loaded, will need to generate on-the-fly when playing
      setIsNarrationReady(true);
      setIsLoading(false);
    };
    
    loadData();
  }, [domainId, navigate, currentDomainKey, currentDomain.name, selectedLanguage]);

  // Load audio from the new batch TTS API
  // This is called first before falling back to old MongoDB cache
  const loadBatchAudio = async () => {
    try {
      const userId = getGenerationUserId();
      if (!userId) {
        console.log('⚠️ No user ID for batch audio');
        return null;
      }
      
      console.log(`📥 Loading ${currentDomainKey} batch audio for all languages...`);
      
      const languages = ['hinglish', 'hindi', 'english'];
      let totalLoaded = 0;
      const texts = { hinglish: '', hindi: '', english: '' };
      
      for (const lang of languages) {
        try {
          const result = await getDomainAudio(userId, currentDomainKey, lang);
          
          if (result?.success && result?.audio) {
            // Convert base64 to blob URL
            const blob = await (await fetch(`data:audio/mp3;base64,${result.audio}`)).blob();
            const blobUrl = URL.createObjectURL(blob);
            
            // Store as section 0 (full narration)
            if (!audioCache.current[lang]) audioCache.current[lang] = {};
            audioCache.current[lang][0] = blobUrl;
            totalLoaded++;
            
            // Store the text if available
            if (result.section_text) {
              texts[lang] = result.section_text;
            }
            
            console.log(`✅ ${currentDomainKey}/${lang} batch audio loaded (${result.audio.length} bytes)`);
          }
        } catch (e) {
          console.log(`⚠️ Failed to load batch audio for ${lang}:`, e.message);
        }
      }
      
      if (totalLoaded > 0) {
        console.log(`✅ Batch audio loaded: ${totalLoaded}/3 languages`);
        return { 
          audioLoaded: true, 
          totalLoaded,
          texts // Return the texts for each language
        };
      }
      
      return null;
    } catch (e) {
      console.log('Batch audio load error:', e.message);
      return null;
    }
  };

  // Load audio from MongoDB - Load DOMAIN-SPECIFIC audio (not unified)
  // Returns { sections: [...], audioLoaded: boolean } or null
  const loadAudioFromMongoDB = async () => {
    // First try the new batch API
    const batchResult = await loadBatchAudio();
    if (batchResult?.audioLoaded) {
      console.log('✅ Using batch-generated audio');
      
      // If we have texts from batch, use them as sections
      if (batchResult.texts) {
        const sections = [];
        // Use hinglish as primary, fall back to english
        const primaryText = batchResult.texts.hinglish || batchResult.texts.english || batchResult.texts.hindi || '';
        if (primaryText) {
          sections.push(primaryText);
        }
        
        // Store ALL language texts
        allTimelineNarrationsRef.current = {
          hinglish: batchResult.texts.hinglish ? [batchResult.texts.hinglish] : [],
          english: batchResult.texts.english ? [batchResult.texts.english] : [],
          hindi: batchResult.texts.hindi ? [batchResult.texts.hindi] : []
        };
        
        return { 
          sections, 
          audioLoaded: true,
          batchTexts: batchResult.texts
        };
      }
      
      return batchResult;
    }
    
    try {
      // Use consistent user ID - MUST match TTS generation user ID
      const userId = getGenerationUserId();
      if (!userId) {
        console.log('⚠️ No user_id found, will generate TTS on play');
        return null;
      }
      
      // Load from domain-specific audio cache
      console.log(`📥 Loading ${currentDomainKey} audio from MongoDB for user: ${userId}`);
      const response = await fetch(`${API_URL}/api/audio-cache/load/${userId}/future-domain/${currentDomainKey}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const audioData = result.data.audio || {};
          const sections = result.data.sections || [];
          
          // Load ALL 3 languages
          const languages = ['hinglish', 'hindi', 'english'];
          let totalLoaded = 0;
          
          for (const lang of languages) {
            if (audioData[lang]) {
              audioCache.current[lang] = {};
              for (const idx of Object.keys(audioData[lang])) {
                try {
                  const blob = await (await fetch(`data:audio/mp3;base64,${audioData[lang][idx]}`)).blob();
                  audioCache.current[lang][idx] = URL.createObjectURL(blob);
                  totalLoaded++;
                } catch (e) {
                  console.log(`⚠️ Failed to load ${lang}[${idx}]`);
                }
              }
              console.log(`✅ ${currentDomainKey} ${lang} audio loaded (${Object.keys(audioCache.current[lang]).length} sections)`);
            }
          }
          
          console.log(`✅ ${currentDomainKey} total audio segments loaded: ${totalLoaded}`);
          
          return { sections, audioLoaded: totalLoaded > 0 };
        }
      }
      
      console.log(`⚠️ No ${currentDomainKey} audio found in MongoDB`);
      return null;
    } catch (e) { 
      console.log('MongoDB audio load error:', e.message); 
      return null;
    }
  };

  // iOS Audio Context unlock - required for WebView audio playback
  const unlockAudioContext = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        console.log('✅ iOS Audio Context unlocked');
      }
    } catch (e) {
      console.log('Audio context unlock not needed:', e.message);
    }
  }, []);

  // Handle sage click
  // SINGLE PLAY/PAUSE TOGGLE - Like PastStoryRevolutionary
  const handlePlayPauseToggle = () => {
    // CRITICAL: Unlock iOS audio on first user tap
    unlockAudioContext();
    
    if (isPlaying) {
      // PAUSE - Save exact position including time within chunk
      const currentTime = audioRef.current?.currentTime || 0;
      
      // CRITICAL: Stop all audio properly
      stopAllAudio();
      isPlayingRef.current = false; // Mark as not playing
      
      setIsPlaying(false);
      
      // Save exact time position for this language
      setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
      
      // SAVE domain position to localStorage when pausing
      saveDomainPosition();
      
      console.log(`⏸️ Paused ${selectedLanguage} at chunk ${playbackPosition[selectedLanguage]}, time: ${currentTime.toFixed(2)}s`);
    } else {
      // PLAY or RESUME
      const currentPos = playbackPosition[selectedLanguage] || 0;
      const currentTime = audioTimePosition[selectedLanguage] || 0;
      const sections = allTimelineNarrationsRef.current[selectedLanguage] || timelineNarrations;
      const totalSections = sections?.length || 0;
      
      console.log(`▶️ Play requested: ${selectedLanguage}, pos=${currentPos}/${totalSections}, time=${currentTime}s, complete=${storyComplete}`);
      
      if (storyComplete || currentPos >= totalSections) {
        // Story complete - replay from beginning
        console.log(`🔄 Replaying ${selectedLanguage} from beginning`);
        setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
        setCurrentSectionIndex(0);
        setStoryComplete(false);
        setCurrentPhase('timeline'); // Changed from 'playing' to show text box
        playStory(selectedLanguage, 0, 0);
      } else if (currentPos > 0 || currentTime > 0) {
        // Resume from saved position AND time
        console.log(`▶️ Resuming ${selectedLanguage} from chunk ${currentPos}, time ${currentTime}s`);
        setCurrentPhase('timeline'); // Changed from 'playing' to show text box
        playStory(selectedLanguage, currentPos, currentTime);
      } else {
        // First play - start from beginning
        console.log(`▶️ Starting ${selectedLanguage} from beginning`);
        setCurrentPhase('intro'); // Use 'intro' for first play
        setShowSkipButton(true);
        playStory(selectedLanguage, 0, 0);
      }
    }
  };

  // Legacy handler for backwards compatibility
  const handleSageClick = () => {
    if (futureMahadashas.length === 0) return;
    handlePlayPauseToggle();
  };

  // PRE-GENERATE ALL TTS for the selected language - called when language changes or domain loads
  const preGenerateAllTTS = async (language, intro, mahadashas) => {
    console.log(`🚀 ULTRA-FAST: Pre-generating ALL TTS for ${language}...`);
    setIsNarrationReady(false);
    setNarrationLoadingProgress(0);
    
    // Use consistent user ID from getGenerationUserId
    const userId = getGenerationUserId() || 'anonymous';
    
    try {
      // First check if we have cached audio in IndexedDB
      const cachedAudio = await LocalAudioCache.getAudioLocal(userId, `domain_${currentDomainKey}`, language);
      if (cachedAudio) {
        console.log(`✅ Found cached audio for ${currentDomainKey}/${language}`);
        audioCache.current[language] = audioCache.current[language] || {};
        audioCache.current[language].fromCache = true;
        setNarrationLoadingProgress(100);
        setIsNarrationReady(true);
        return;
      }
      
      // Build domain texts for batch generation
      const domainTexts = {};
      domainTexts[currentDomainKey] = {};
      
      // Add intro text
      if (intro && intro.length > 50) {
        domainTexts[currentDomainKey][language] = intro;
      }
      
      // Add mahadasha narrations
      for (const md of mahadashas) {
        const narrationText = generateDomainNarration(md, language);
        if (narrationText && narrationText.length > 50) {
          // Append to the text
          if (domainTexts[currentDomainKey][language]) {
            domainTexts[currentDomainKey][language] += ' ' + narrationText;
          } else {
            domainTexts[currentDomainKey][language] = narrationText;
          }
        }
      }
      
      setNarrationLoadingProgress(20);
      
      // Use ULTRA-FAST batch generation
      console.log(`🎙️ Calling ultra-fast batch for ${currentDomainKey}/${language}...`);
      const result = await generateAllAudioUltraFast(userId, domainTexts, (progress) => {
        setNarrationLoadingProgress(20 + Math.round(progress.progress * 0.7));
      });
      
      if (result.success && result.audioResults?.[currentDomainKey]?.[language]) {
        const audioData = result.audioResults[currentDomainKey][language];
        if (audioData.success && audioData.audio_base64) {
          // Save to IndexedDB cache
          await LocalAudioCache.saveAudioLocal(userId, `domain_${currentDomainKey}`, language, audioData.audio_base64);
          console.log(`✅ Ultra-fast TTS complete for ${currentDomainKey}/${language}`);
        }
      }
      
      setNarrationLoadingProgress(100);
      setIsNarrationReady(true);
      console.log(`✅ ALL TTS ready for ${language}!`);
      
    } catch (e) {
      console.error('Error pre-generating TTS:', e);
      // Don't fall back to slow individual generation - just mark as ready
      // Audio will be generated on-demand if user clicks play
      console.log('⚠️ TTS batch failed - audio will generate on-demand');
      setNarrationLoadingProgress(100);
      setIsNarrationReady(true);
    }
  };
  
  // Legacy fallback function - NOW JUST MARKS AS READY (no slow individual generation)
  const preGenerateAllTTSLegacy = async (language, intro, mahadashas) => {
    console.log(`⚡ Fast fallback: Skipping slow individual TTS, marking as ready for ${language}`);
    // Don't generate individual TTS - too slow (was causing 2+ minute delays)
    // Just mark as ready and let on-demand generation handle missing audio
    setNarrationLoadingProgress(100);
    setIsNarrationReady(true);
    console.log(`✅ TTS marked ready for ${language} (audio will generate on-demand if needed)`);
  };

  // Helper function to play audio with abort check and optional startTime
  const playAudioWithAbortCheck = async (audioUrl, onComplete, startTime = 0) => {
    return new Promise((resolve, reject) => {
      if (abortRef.current) {
        console.log('⏹️ Audio playback aborted before start');
        resolve();
        return;
      }
      
      console.log(`🔊 Creating audio from URL: ${audioUrl?.substring(0, 50)}...`);
      
      // Stop any existing audio first - CRITICAL for clean playback
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          audioRef.current.load();
        } catch (e) { /* Ignore cleanup errors */ }
        audioRef.current = null;
      }
      if (window.currentDomainAudio) {
        try {
          window.currentDomainAudio.pause();
          window.currentDomainAudio.currentTime = 0;
          window.currentDomainAudio.src = '';
        } catch (e) { /* Ignore cleanup errors */ }
        window.currentDomainAudio = null;
      }
      
      // Create iOS/Android/Web compatible audio element
      const audio = new Audio();
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.preload = 'auto';
      audio.src = audioUrl;
      
      audioRef.current = audio;
      window.currentDomainAudio = audio; // Track globally for domain switch cleanup
      
      // Set start time when metadata is loaded (for resume functionality)
      audio.onloadedmetadata = () => {
        console.log(`📀 Audio loaded: duration=${audio.duration?.toFixed(1)}s`);
        if (startTime > 0 && startTime < audio.duration) {
          audio.currentTime = startTime;
          console.log(`⏩ Starting audio from ${startTime.toFixed(1)}s`);
        }
      };
      
      // Log when audio actually starts playing
      audio.onplay = () => {
        console.log('▶️ Audio playback STARTED');
      };
      
      audio.oncanplay = () => {
        console.log('🎵 Audio can play');
      };
      
      // Check abort periodically during playback
      const abortCheckInterval = setInterval(() => {
        if (abortRef.current) {
          console.log('⏹️ Audio playback aborted during play');
          clearInterval(abortCheckInterval);
          try {
            audio.pause();
            audio.src = '';
          } catch (e) { /* Ignore cleanup errors */ }
          resolve();
        }
      }, 100);
      
      audio.onended = () => {
        console.log('🏁 Audio playback ENDED');
        clearInterval(abortCheckInterval);
        // Reset time position since chunk completed
        setAudioTimePosition(prev => ({ ...prev, [currentLanguageRef.current]: 0 }));
        if (onComplete) onComplete();
        resolve();
      };
      
      audio.onerror = (e) => {
        console.error('❌ Audio error:', e?.message || e);
        clearInterval(abortCheckInterval);
        reject(e);
      };
      
      // Try to play
      console.log('🎯 Calling audio.play()...');
      audio.play()
        .then(() => {
          console.log('✅ audio.play() promise resolved');
        })
        .catch((e) => {
          console.error('❌ audio.play() failed:', e.message);
          clearInterval(abortCheckInterval);
          reject(e);
        });
    });
  };

  // Play story - supports starting from specific section and time for resume
  const playStory = async (lang, startFromIndex = 0, startTime = 0) => {
    // CRITICAL: Stop any existing audio first
    stopAllAudio();
    
    // Small delay to ensure cleanup
    await new Promise(r => setTimeout(r, 50));
    
    abortRef.current = false; // Reset abort flag
    isPlayingRef.current = true; // Mark as playing
    setIsPlaying(true);
    setShowSkipButton(true);
    setStoryComplete(false);
    currentLanguageRef.current = lang;
    
    // For domain reels, we play ALL sections as one continuous story
    const sectionsFromRef = allTimelineNarrationsRef.current[lang] || [];
    const sectionsFromState = timelineNarrations || [];
    const fallbackSections = introText ? [introText] : [];
    
    const sections = sectionsFromRef.length > 0 ? sectionsFromRef 
                   : sectionsFromState.length > 0 ? sectionsFromState 
                   : fallbackSections;
    
    console.log(`🎬 Playing ${currentDomain.name} story (${lang})`);
    console.log(`   - sectionsFromRef: ${sectionsFromRef.length}`);
    console.log(`   - sectionsFromState: ${sectionsFromState.length}`);
    console.log(`   - fallbackSections: ${fallbackSections.length}`);
    console.log(`   - USING: ${sections.length} sections, starting from ${startFromIndex}, time: ${startTime}s`);
    
    // If no sections at all, show error and stop
    if (sections.length === 0) {
      console.error(`❌ No content to play for ${currentDomain.name} in ${lang}`);
      setDisplayedText(`No ${lang} narration available for ${currentDomain.name}. Please regenerate your story.`);
      setIsPlaying(false);
      setStoryComplete(true);
      return;
    }
    
    // Play sections sequentially starting from startFromIndex
    for (let idx = startFromIndex; idx < sections.length; idx++) {
      // Check abort at start of each section
      if (abortRef.current) {
        console.log(`⏹️ Story aborted at section ${idx}`);
        break;
      }
      
      const sectionText = typeof sections[idx] === 'string' ? sections[idx] : sections[idx]?.text || '';
      if (!sectionText || sectionText.length < 20) { // Reduced from 50
        console.log(`⚠️ Skipping empty section ${idx}`);
        continue;
      }
      
      // Remove emojis from text
      const cleanText = sectionText.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏/gu, '').trim();
      
      console.log(`📝 Playing section ${idx + 1}/${sections.length}: ${cleanText.substring(0, 80)}...`);
      
      // Update UI and track position BEFORE playing
      setSelectedYear(idx);
      setCurrentSectionIndex(idx);
      setPlaybackPosition(prev => ({ ...prev, [lang]: idx }));
      setCurrentNarrationText(cleanText);
      
      // SAME AS PAST STORY: Show full text immediately - no word-by-word reveal
      updateDisplayedText(cleanText);
      
      // Try to get cached audio first - use the correct language
      // For batch-generated audio, it's stored at index 0 as the FULL narration
      let audioUrl = audioCache.current[lang]?.[0] || audioUrlCacheRef.current[lang]?.[idx] || audioCache.current[lang]?.[idx];
      
      // If we have batch audio at index 0, use it for all sections (it's the full narration)
      if (!audioUrl && audioCache.current[lang]?.[0]) {
        audioUrl = audioCache.current[lang][0];
        console.log(`✅ Using batch audio for section ${idx + 1}`);
      }
      
      // GLOBAL STORE: Check for per-chunk audio saved by LoadingDomainReel (0ms lookup)
      if (!audioUrl && cleanText.length > 20) {
        const globalAudio = getFromGlobalStore(lang, cleanText);
        if (globalAudio) {
          audioUrl = globalAudio.blobUrl || globalAudio.dataUrl;
          console.log(`⚡ Global store hit for section ${idx + 1}`);
          if (!audioCache.current[lang]) audioCache.current[lang] = {};
          audioCache.current[lang][idx] = audioUrl;
        }
      }
      
      // Try to get audio from IndexedDB cache (ultra-fast)
      if (!audioUrl && cleanText.length > 20) {
        console.log(`🔍 Checking IndexedDB cache for ${currentDomainKey}/${lang}...`);
        try {
          const userId = getGenerationUserId() || 'anonymous';
          const cachedAudio = await LocalAudioCache.getAudioLocal(userId, `domain_${currentDomainKey}`, lang);
          if (cachedAudio) {
            const blob = await (await fetch(`data:audio/mp3;base64,${cachedAudio}`)).blob();
            audioUrl = URL.createObjectURL(blob);
            console.log(`✅ Loaded audio from IndexedDB cache for ${currentDomainKey}/${lang}`);
            // Cache it in memory too
            if (!audioCache.current[lang]) audioCache.current[lang] = {};
            audioCache.current[lang][0] = audioUrl;
          }
        } catch (e) {
          console.log('IndexedDB cache miss:', e.message);
        }
      }
      
      // If still no audio, skip slow generation - user should wait for background generation
      if (!audioUrl && cleanText.length > 20) {
        console.log(`⚠️ No cached audio for section ${idx + 1} - skipping slow TTS generation`);
        // Don't call slow generateTTSAudio - let user know audio is generating
      } else if (audioUrl) {
        console.log(`✅ Using cached audio for section ${idx + 1}`);
      }
      
      // Calculate fallback duration (using different var name to avoid conflict)
      const sectionWordCount = cleanText.split(/\s+/).length;
      const estimatedDuration = Math.max(sectionWordCount * 430, 10000);
      
      // Use start time only for the first section when resuming
      const sectionStartTime = (idx === startFromIndex) ? startTime : 0;
      
      console.log(`🔈 Attempting to play section ${idx + 1}, audioUrl exists: ${!!audioUrl}`);
      
      if (audioUrl) {
        try {
          // Play the audio with startTime for resume support
          console.log(`▶️ Starting playAudioWithAbortCheck for section ${idx + 1}...`);
          await playAudioWithAbortCheck(audioUrl, () => {
            console.log(`✅ Section ${idx + 1} audio completed`);
          }, sectionStartTime);
          console.log(`🏁 playAudioWithAbortCheck resolved for section ${idx + 1}`);
        } catch (e) {
          console.error(`❌ Audio error for section ${idx + 1}:`, e.message);
          if (!abortRef.current) {
            console.log(`⏳ Waiting ${estimatedDuration}ms as fallback...`);
            await new Promise(r => setTimeout(r, estimatedDuration));
          }
        }
      } else {
        // No audio - just show text for estimated duration
        console.log(`⚠️ No audio for section ${idx + 1}, waiting ${estimatedDuration}ms`);
        if (!abortRef.current) {
          await new Promise(r => setTimeout(r, estimatedDuration));
        }
      }
      
      // Check abort after section
      if (abortRef.current) break;
      
      // Brief pause between sections
      if (idx < sections.length - 1) {
        await new Promise(r => setTimeout(r, 500));
        if (abortRef.current) break;
      }
    }
    
    // Check if we completed the story (not aborted)
    if (!abortRef.current) {
      setIsPlaying(false);
      setShowSkipButton(false);
      setStoryComplete(true);
      setCurrentPhase('complete');
      // Save final position as completed
      setPlaybackPosition(prev => ({ ...prev, [lang]: sections.length }));
      setAudioTimePosition(prev => ({ ...prev, [lang]: 0 }));
      console.log(`✅ ${currentDomain.name} story (${lang}) completed!`);
    }
  };

  // Skip - COMPLETE cleanup and mark as complete
  const handleSkip = () => {
    // CRITICAL: Stop all audio completely
    stopAllAudio();
    
    // Mark current language as complete
    const sections = allTimelineNarrationsRef.current[selectedLanguage] || timelineNarrations;
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: sections?.length || 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    
    setIsPlaying(false);
    setShowSkipButton(false);
    setStoryComplete(true);
    setCurrentPhase('complete');
  };

  // Replay - COMPLETE cleanup and restart from beginning
  const handleReplay = () => {
    // CRITICAL: Stop all audio first
    stopAllAudio();
    
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentSectionIndex(0);
    setStoryComplete(false);
    
    // Small delay then start playing
    setTimeout(() => {
      playStory(selectedLanguage, 0, 0);
    }, 100);
  };

  // Language change - COMPLETELY STOP current, switch to new (keep positions per language)
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    
    // Save current time position before switching
    if (isPlaying && audioRef.current) {
      const currentTime = audioRef.current.currentTime || 0;
      setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
    }
    
    // CRITICAL: Completely stop all audio
    stopAllAudio();
    setIsPlaying(false);
    
    const oldPos = playbackPosition[selectedLanguage] || 0;
    const oldTime = audioTimePosition[selectedLanguage] || 0;
    const newPos = playbackPosition[newLang] || 0;
    const newTime = audioTimePosition[newLang] || 0;
    
    console.log(`🔄 Switching: ${selectedLanguage}[chunk ${oldPos}, time ${oldTime.toFixed(1)}s] → ${newLang}[chunk ${newPos}, time ${newTime.toFixed(1)}s]`);
    
    // Switch language
    setSelectedLanguage(newLang);
    currentLanguageRef.current = newLang;
    
    // Check if new language is complete
    const newNarrations = allTimelineNarrationsRef.current[newLang] || [];
    const isComplete = newPos >= (newNarrations?.length || 0);
    setStoryComplete(isComplete);
    
    // Update intro for new language
    const userName = personName || 'User';
    let newIntro = allIntrosRef.current[newLang] || '';
    if (!newIntro || newIntro.length < 50) {
      if (newLang === 'hindi') {
        newIntro = `नमस्ते ${userName}! मैं आपको ${currentDomain.name} की भविष्य की पूरी कहानी सुनाने जा रहा हूं।`;
      } else if (newLang === 'english') {
        newIntro = `Hello ${userName}! Let me tell you about your complete ${currentDomain.name} future.`;
      } else {
        newIntro = `Namaste ${userName}! Main aapko ${currentDomain.name} ki bhavishya ki poori kahani sunane ja raha hoon.`;
      }
    }
    newIntro = newIntro.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏/gu, '').trim();
    setIntroText(newIntro);
    
    // Update timeline narrations for new language
    setTimelineNarrations(newNarrations);
    
    // Update displayed text and position for new language at its saved position
    if (newNarrations.length > 0) {
      const displayPos = Math.min(newPos, newNarrations.length - 1);
      const sectionText = typeof newNarrations[displayPos] === 'string' ? newNarrations[displayPos] : newNarrations[displayPos]?.text || '';
      setDisplayedText(sectionText.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏/gu, '').trim());
      setCurrentSectionIndex(displayPos);
    } else {
      setDisplayedText('');
      setCurrentSectionIndex(0);
    }
    
    console.log(`📝 Switched to ${newLang}: narrations=${newNarrations.length}, complete=${isComplete}`);
    
    // Set phase based on completion
    setCurrentPhase(isComplete ? 'complete' : 'ready');
    setIsNarrationReady(true);
  };

  const currentPeriod = futureMahadashas[selectedYear] || {};

  // SKIP loading screen entirely if unified story data exists
  // User has already generated/paid - show content immediately
  const hasUnifiedData = typeof window !== 'undefined' && localStorage.getItem('unified_story_data');
  
  if (isLoading && !hasUnifiedData) {
    // Only show loading if truly no data exists
    return (
      <div style={{ minHeight: '100vh', background: '#020712', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.p animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}
          style={{ color: currentDomain.color, fontSize: 'clamp(13px, 3.5vw, 16px)' }}>Loading {currentDomain.name}...</motion.p>
      </div>
    );
  }

  // Show locked screen if user hasn't paid
  if (isLocked) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: `radial-gradient(ellipse at center, ${currentDomain.color}10 0%, #020712 100%)`,
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ 
          width: '100px', height: '100px', borderRadius: '50%', 
          background: `${currentDomain.color}15`, 
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '24px',
          border: `2px solid ${currentDomain.color}40`
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={currentDomain.color} strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 style={{ color: currentDomain.color, fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 700, marginBottom: '12px' }}>
          {currentDomain.name} Future Story
        </h2>
        <p style={{ color: '#9CA3AF', fontSize: 'clamp(12px, 3.2vw, 14px)', textAlign: 'center', maxWidth: '300px', marginBottom: '32px' }}>
          Unlock your complete {currentDomain.name.toLowerCase()} future predictions with all {futureMahadashas.length || 7} Mahadashas
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/payment-plans')}
          style={{
            padding: '14px 40px', borderRadius: '25px',
            background: `linear-gradient(135deg, ${currentDomain.color}, ${currentDomain.color}cc)`,
            border: 'none', cursor: 'pointer',
            fontSize: 'clamp(13px, 3.5vw, 16px)', fontWeight: 600, color: '#020712',
            boxShadow: `0 0 25px ${currentDomain.color}40`
          }}
        >
          Unlock Now
        </motion.button>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            marginTop: '16px', padding: '10px 20px',
            background: 'transparent', border: `1px solid ${currentDomain.color}40`,
            borderRadius: '20px', color: currentDomain.color,
            fontSize: 'clamp(12px, 3.2vw, 14px)', cursor: 'pointer'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div 
      {...swipeHandlers}
      style={{
        minHeight: '100vh',
        background: `radial-gradient(ellipse at center, ${currentDomain.color}15 0%, #020712 100%)`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        touchAction: 'pan-y'
      }}
    >
      {/* Swipe Animation Overlay */}
      <AnimatePresence>
        {swipeDirection && (
          <motion.div
            initial={{ opacity: 0, x: swipeDirection === 'left' ? 100 : -100 }}
            animate={{ opacity: 0.3, x: 0 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: `linear-gradient(${swipeDirection === 'left' ? '90deg' : '270deg'}, transparent, ${currentDomain.color}40)`,
              pointerEvents: 'none'
            }}
          />
        )}
      </AnimatePresence>

      {/* Stars */}
      <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(2px 2px at 20% 30%, white, transparent), radial-gradient(1px 1px at 60% 70%, white, transparent)', backgroundSize: '100vw 100vh', opacity: 0.3, pointerEvents: 'none' }} />

      {/* Swipe Navigation Arrows */}
      {currentDomainIndex > 0 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          whileHover={{ opacity: 1, scale: 1.1 }}
          onClick={() => navigateToDomain(-1)}
          style={{
            position: 'fixed', left: '8px', top: '50%', transform: 'translateY(-50%)',
            zIndex: 200, background: 'rgba(10, 22, 40, 0.9)', border: `1px solid ${currentDomain.color}40`,
            borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <ChevronLeft size={20} color={currentDomain.color} />
        </motion.button>
      )}
      
      {currentDomainIndex < DOMAIN_ORDER.length - 1 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          whileHover={{ opacity: 1, scale: 1.1 }}
          onClick={() => navigateToDomain(1)}
          style={{
            position: 'fixed', right: '8px', top: '50%', transform: 'translateY(-50%)',
            zIndex: 200, background: 'rgba(10, 22, 40, 0.9)', border: `1px solid ${currentDomain.color}40`,
            borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <ChevronRight size={20} color={currentDomain.color} />
        </motion.button>
      )}

      {/* Back button - COMPLETE cleanup before navigating */}
      <button onClick={() => {
        completeAudioCleanup();
        navigate('/home');
      }} style={{
        position: 'fixed',
        top: 'calc(12px + env(safe-area-inset-top, 0px))',
        left: '12px', zIndex: 200,
        background: 'rgba(10, 22, 40, 0.8)', border: `1px solid ${currentDomain.color}40`,
        borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <ArrowLeft size={20} color={currentDomain.color} />
      </button>

      {/* Domain Indicator Dots */}
      <div style={{
        position: 'fixed',
        bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: '8px', zIndex: 200, padding: '10px 16px',
        background: 'rgba(10, 22, 40, 0.95)', backdropFilter: 'blur(15px)',
        borderRadius: '25px', border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {DOMAIN_ORDER.map((domainKey, idx) => {
          const domain = DOMAINS[domainKey];
          const isActive = domainKey === currentDomainKey;
          return (
            <motion.button
              key={domainKey}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (!isActive) {
                  // COMPLETE cleanup before switching domains
                  completeAudioCleanup();
                  navigate(`/domain-reels/${domainKey}`, { replace: true });
                }
              }}
              style={{
                width: isActive ? '28px' : '10px',
                height: '10px',
                borderRadius: '5px',
                background: isActive ? domain.color : 'rgba(255,255,255,0.3)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isActive ? `0 0 10px ${domain.color}` : 'none'
              }}
              title={domain.name}
            />
          );
        })}
      </div>

      {/* Language Pills */}
      <div style={{
        position: 'fixed',
        top: 'calc(12px + env(safe-area-inset-top, 0px))',
        left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: '6px', zIndex: 200,
        background: 'rgba(10, 22, 40, 0.95)', backdropFilter: 'blur(15px)',
        padding: '8px 14px', borderRadius: '22px', border: `1px solid ${currentDomain.color}40`,
        // Hard cap so the pill bar can never collide with the back button (40px wide + 12px left)
        // even on the smallest iPhone SE width (320px portrait).
        maxWidth: 'calc(100vw - 110px)'
      }}>
        {[{ key: 'hindi', label: 'हिंदी' }, { key: 'hinglish', label: 'Hinglish' }, { key: 'english', label: 'English' }].map(lang => (
          <button key={lang.key} onClick={() => handleLanguageChange(lang.key)}
            style={{
              padding: '6px 12px', borderRadius: '16px', border: 'none',
              background: selectedLanguage === lang.key ? `${currentDomain.color}35` : 'transparent',
              color: selectedLanguage === lang.key ? currentDomain.color : '#9CA3AF',
              fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: 600, cursor: 'pointer'
            }}>{lang.label}</button>
        ))}
      </div>

      {/* Title - Clean without emojis */}
      <div style={{
        textAlign: 'center',
        paddingTop: 'calc(70px + env(safe-area-inset-top, 0px))',
        paddingBottom: '5px'
      }}>
        <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 700, color: currentDomain.color, margin: 0, textShadow: `0 0 25px ${currentDomain.color}60` }}>
          {currentDomain.name}
        </h1>
        <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: '#9CA3AF', margin: '4px 0 0' }}>Future Story • {futureMahadashas.length} Mahadashas</p>
      </div>

      {/* Sage Section with SINGLE Play/Pause Button like PastStory */}
      <div style={{ textAlign: 'center', padding: '10px 20px', position: 'relative' }}>
        <div style={{ cursor: isNarrationReady ? 'pointer' : 'default' }}>
          <motion.img src="/sage-final.png" alt="Sage"
            animate={{ 
              y: isPlaying ? [0, -8, 0] : [0, -4, 0],
              filter: isPlaying ? `drop-shadow(0 0 40px ${currentPeriod.color || currentDomain.color})` : `drop-shadow(0 0 20px ${currentDomain.color}40)`
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ width: '120px', height: 'auto', margin: '0 auto' }}
          />
          
          {/* SINGLE PLAY/PAUSE BUTTON - Always visible when ready */}
          {isNarrationReady && currentPhase !== 'complete' && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
              onClick={(e) => e.stopPropagation()} // Prevent swipe interference
            >
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('🎯 Play button clicked');
                  handlePlayPauseToggle();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  background: `linear-gradient(135deg, ${currentDomain.color}, ${currentDomain.color}cc)`,
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 25px ${currentDomain.color}60`,
                  zIndex: 100 // Ensure button is on top
                }}
              >
                {isPlaying ? (
                  // Pause icon
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{ width: '5px', height: '20px', background: '#020712', borderRadius: '2px' }} />
                    <div style={{ width: '5px', height: '20px', background: '#020712', borderRadius: '2px' }} />
                  </div>
                ) : (
                  // Play icon
                  <div style={{
                    width: 0, height: 0,
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    borderLeft: '18px solid #020712',
                    marginLeft: '4px'
                  }} />
                )}
              </motion.button>
              <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: currentDomain.color, fontWeight: 600, margin: 0, textAlign: 'center' }}>
                {isPlaying 
                  ? 'Tap to Pause'
                  : storyComplete 
                    ? 'Tap to Replay'
                    : playbackPosition[selectedLanguage] > 0 
                      ? 'Tap to Resume'
                      : `Tap to Start ${currentDomain.name}`
                }
              </p>
            </motion.div>
          )}
          
          {/* Loading state when not ready - ONLY show if no unified data */}
          {!isNarrationReady && currentPhase !== 'complete' && !hasUnifiedData && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
            >
              <motion.button
                disabled
                style={{
                  width: '60px', height: '60px', borderRadius: '50%',
                  background: `linear-gradient(135deg, ${currentDomain.color}50, ${currentDomain.color}30)`,
                  border: 'none', cursor: 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  style={{
                    width: '24px', height: '24px',
                    border: `3px solid transparent`,
                    borderTopColor: '#020712',
                    borderRadius: '50%'
                  }}
                />
              </motion.button>
              <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: currentDomain.color, fontWeight: 600, margin: 0, textAlign: 'center' }}>
                Preparing...
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Narration Box - SAME AS PAST STORY - Clean, consistent text display */}
      <AnimatePresence mode="wait">
        {displayedText && (
          <motion.div 
            key={`text-${currentPhase}-${selectedYear}`}
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ margin: '10px auto 0', padding: '0 12px', maxWidth: '100%', width: '100%' }}>
            
            {/* Arrow pointer */}
            <div style={{
              width: 0, height: 0, margin: '0 auto',
              borderLeft: '10px solid transparent', borderRight: '10px solid transparent',
              borderBottom: `10px solid ${currentPhase === 'intro' ? currentDomain.color + '40' : (currentPeriod.color || currentDomain.color) + '40'}`
            }} />
            
            {/* Text container - SAME STYLE AS PAST STORY */}
            <div style={{
              padding: 'clamp(12px, 3vw, 18px)',
              background: `linear-gradient(135deg, ${currentPhase === 'intro' ? currentDomain.color + '18' : (currentPeriod.color || currentDomain.color) + '18'}, rgba(2, 7, 18, 0.95))`,
              backdropFilter: 'blur(20px)', 
              borderRadius: 'clamp(12px, 3vw, 16px)',
              border: `1.5px solid ${currentPhase === 'intro' ? currentDomain.color + '50' : (currentPeriod.color || currentDomain.color) + '50'}`,
              minHeight: 'clamp(80px, 16vh, 120px)',
              maxHeight: 'clamp(160px, 30vh, 220px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <p style={{ 
                fontSize: 'clamp(13px, 3.5vw, 16px)', 
                lineHeight: '1.8', 
                color: '#F0F0F0', 
                margin: 0, 
                textAlign: 'center'
              }}>
                {displayedText}
              </p>
            </div>

            {/* Current Mahadasha Indicator - Compact, SAME AS PAST STORY */}
            {currentPhase === 'timeline' && selectedYear >= 0 && currentPeriod.lord && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                style={{ 
                  textAlign: 'center', 
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span style={{ 
                  fontSize: 'clamp(20px, 5vw, 28px)', 
                  textShadow: `0 0 15px ${currentPeriod.color || currentDomain.color}` 
                }}>
                  {currentPeriod.icon || PLANET_ICONS[currentPeriod.lord] || '☉'}
                </span>
                <p style={{
                  fontSize: 'clamp(10px, 2.8vw, 12px)',
                  fontWeight: 600,
                  color: currentPeriod.color || currentDomain.color,
                  margin: 0
                }}>
                  {currentPeriod.lord} Mahadasha • {currentPeriod.years}
                </p>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline */}
      <div style={{ flexShrink: 0, padding: '8px 6px 12px' }}>
        <div style={{ position: 'relative', height: 'clamp(60px, 12vh, 80px)', width: '100%' }}>
          <div style={{
            position: 'absolute', top: '50%', left: '5%', right: '5%',
            height: '2px', background: `${currentDomain.color}25`, transform: 'translateY(-50%)'
          }} />

          <motion.div
            animate={{
              scaleX: currentPhase === 'timeline' && selectedYear >= 0 ? (selectedYear + 1) / futureMahadashas.length : 0,
              opacity: currentPhase === 'timeline' ? 1 : 0
            }}
            style={{
              position: 'absolute', top: '50%', left: '5%', right: '5%',
              height: '3px', background: `linear-gradient(90deg, ${currentDomain.color}, ${currentDomain.color})`,
              transformOrigin: 'left', borderRadius: '2px', transform: 'translateY(-50%)',
              boxShadow: `0 0 12px ${currentDomain.color}`
            }}
          />

          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '0 5%',
            position: 'absolute', top: 0, left: 0, right: 0, height: '100%', alignItems: 'center'
          }}>
            {futureMahadashas.map((period, idx) => {
              const isActive = currentPhase === 'timeline' && selectedYear === idx;
              const planetColor = period.color || PLANET_COLORS[period.lord] || currentDomain.color;
              
              // Adjust years to show from current year (2026) for display
              let displayYears = period.years || '';
              if (displayYears && displayYears.includes('-')) {
                const [startYear, endYear] = displayYears.split('-').map(y => parseInt(y.trim()));
                const currentYear = new Date().getFullYear();
                const adjustedStart = startYear < currentYear ? currentYear : startYear;
                displayYears = `${adjustedStart}-${endYear}`;
              }
              
              return (
                <div key={idx} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  position: 'relative', zIndex: isActive ? 50 : 10, minWidth: isActive ? '50px' : '30px'
                }}>
                  {isActive && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      style={{ position: 'absolute', top: '-35px', fontSize: 'clamp(18px, 5vw, 24px)', color: planetColor, textShadow: `0 0 15px ${planetColor}` }}>
                      {period.icon || PLANET_ICONS[period.lord]}
                    </motion.div>
                  )}
                  
                  <motion.div
                    animate={{ width: isActive ? '55px' : '16px', height: isActive ? '55px' : '16px', borderColor: isActive ? planetColor : currentDomain.color }}
                    style={{
                      borderRadius: '50%',
                      background: isActive ? `radial-gradient(circle, ${planetColor}50, ${planetColor}10)` : currentDomain.color,
                      border: `2px solid ${isActive ? planetColor : currentDomain.color}`,
                      boxShadow: isActive ? `0 0 25px ${planetColor}` : `0 0 8px ${currentDomain.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: isActive ? '9px' : '0px', fontWeight: 700, color: '#fff'
                    }}
                  >
                    {isActive && period.lord?.substring(0, 3)}
                    {isActive && isPlaying && (
                      <motion.div animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        style={{ position: 'absolute', inset: '-5px', borderRadius: '50%', border: `2px solid ${planetColor}` }}
                      />
                    )}
                  </motion.div>
                  
                  <span style={{
                    fontSize: '7px', color: isActive ? planetColor : currentDomain.color,
                    marginTop: isActive ? '8px' : '4px', fontWeight: 600, textAlign: 'center',
                    opacity: currentPhase === 'timeline' && !isActive ? 0.4 : 0.9, whiteSpace: 'nowrap'
                  }}>
                    {displayYears}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Complete buttons */}
      {currentPhase === 'complete' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: 'center', padding: '20px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={handleReplay} style={{
            padding: '14px 24px', fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: 700,
            background: 'transparent', border: `2px solid ${currentDomain.color}`,
            borderRadius: '25px', color: currentDomain.color, cursor: 'pointer'
          }}>Replay Story</button>
          <button onClick={() => navigate('/home')} style={{
            padding: '14px 30px', fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: 700,
            background: `linear-gradient(135deg, ${currentDomain.color}, ${currentDomain.color}cc)`,
            border: 'none', borderRadius: '25px', color: '#020712', cursor: 'pointer'
          }}>Continue →</button>
        </motion.div>
      )}
    </div>
  );
};

export default DomainReelsStory;
